import type { ReactNode } from "react";
import { Link } from "react-router";
import { cx } from "styled-system/css";
import { ak } from "./admin-styles";

// Admin page pieces (settings, tools, labels), styled by ./admin-styles.

export function PageHead({
  back,
  kicker,
  title,
  dek,
  actions,
}: {
  back?: { to: string; label: string };
  kicker?: ReactNode;
  title: ReactNode;
  dek?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <>
      {back && (
        <Link to={back.to} className={ak.back}>
          ← {back.label}
        </Link>
      )}
      {kicker && <div className={ak.kicker}>{kicker}</div>}
      <div
        className={ak.headRow}
        style={kicker ? undefined : { marginTop: back ? 14 : 0 }}
      >
        <div>
          <h1 className={ak.headline}>{title}</h1>
          {dek && <p className={ak.dek}>{dek}</p>}
        </div>
        {actions && <div className={ak.headActions}>{actions}</div>}
      </div>
    </>
  );
}

export function Section({
  kicker,
  title,
  dek,
  actions,
  children,
  className,
}: {
  kicker?: ReactNode;
  title?: ReactNode;
  dek?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx(ak.sect, className)}>
      {(kicker || title || dek || actions) && (
        <div className={ak.sectHead}>
          <div>
            {kicker && <div className={ak.sectKicker}>{kicker}</div>}
            {title && <h2 className={ak.h2}>{title}</h2>}
            {dek && <p className={ak.sectDek}>{dek}</p>}
          </div>
          {actions && <div className={ak.headActions}>{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export type StatusKind = "ok" | "off" | "fail" | "busy" | "wait";

const GLYPH: Record<StatusKind, string> = {
  ok: "●",
  off: "○",
  fail: "▲",
  busy: "◆",
  wait: "◇",
};

/** Status as a glyph plus text: ● connected, ○ off, ▲ failing. */
export function Status({
  kind,
  children,
  className,
}: {
  kind: StatusKind;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cx(ak.status, className)} data-kind={kind}>
      <span data-glyph aria-hidden>
        {GLYPH[kind]}
      </span>
      <span>{children}</span>
    </span>
  );
}

export function Banner({
  tone = "ok",
  children,
  onDismiss,
}: {
  tone?: "ok" | "caution";
  children: ReactNode;
  onDismiss?: () => void;
}) {
  return (
    <div
      className={ak.banner}
      data-tone={tone}
      role={tone === "caution" ? "alert" : "status"}
    >
      <span data-glyph aria-hidden>
        {tone === "caution" ? "▲" : "●"}
      </span>
      <span>{children}</span>
      {onDismiss && (
        <button
          type="button"
          className={ak.bannerDismiss}
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Outlined ink label. The swatch keeps the color the user picked for it. */
export function Tag({
  name,
  color,
  className,
}: {
  name: ReactNode;
  color?: string | null;
  className?: string;
}) {
  return (
    <span className={cx(ak.tag, className)}>
      {color && (
        <span
          className={ak.swatch}
          style={{ backgroundColor: color }}
          aria-hidden
        />
      )}
      {name}
    </span>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className={ak.switchWrap} title={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={ak.switch} data-checked={checked} aria-hidden />
    </label>
  );
}

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={ak.overlay} onClick={onClose}>
      <div
        className={ak.modal}
        style={wide ? { maxWidth: 560 } : undefined}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={ak.modalHead}>
          <h2 className={ak.modalTitle}>{title}</h2>
          <button
            type="button"
            className={ak.modalClose}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Mono local timestamp, e.g. "Oct 23, 14:05". */
export function Time({ iso, withYear }: { iso: string; withYear?: boolean }) {
  const d = new Date(iso);
  const text = d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return (
    <time dateTime={iso} className={ak.mono}>
      {text}
    </time>
  );
}
